import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import axios from "axios";
import {
  DndContext, DragOverlay, PointerSensor, TouchSensor, useSensor, useSensors, pointerWithin,
} from "@dnd-kit/core";
import { format } from "date-fns";
import { toast } from "sonner";
import { CalendarDays, RefreshCw, Waves, Users, CheckCircle2, Loader2, CircleUser, Printer, AlertTriangle, Search, BarChart3, MapPin, Plus, Eraser, MoreHorizontal } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { AssignSheet } from "./AssignSheet";
import { useIsMobile } from "../hooks/use-mobile";
import { usePullToRefresh } from "../hooks/use-pull-to-refresh";
import { GuestCard, TYPE_META, clientKey } from "./GuestCard";
import { LoungeTile } from "./LoungeTile";
import { PrintSheet } from "./PrintSheet";
import { RecapDialog } from "./RecapDialog";
import { AddSpotDialog } from "./AddSpotDialog";
import { EditSpotDialog } from "./EditSpotDialog";
import { GuestDetailDialog } from "./GuestDetailDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = "https://customer-assets.emergentagent.com/job_beach-lounge-hub/artifacts/65plynsu_DJI_20260612182230_0011_D.jpg";
const SAND_BG = "https://customer-assets.emergentagent.com/job_beach-lounge-hub/artifacts/euxuitcn_e7268e79-206d-452a-852d-061a43a83fa5.JPG";

const TYPE_ORDER = ["large", "medium", "small", "umbrella"];
const TILE_WIDTH = { large: "md:w-44", medium: "md:w-40", small: "md:w-44", umbrella: "md:w-44" };
const MOBILE_COLS = { large: "grid-cols-2", medium: "grid-cols-2", small: "grid-cols-2", umbrella: "grid-cols-2" };

export default function BeachBoard() {
  const [date, setDate] = useState(new Date());
  const [data, setData] = useState({ reservations: [], allocations: [], lounges: [], notes: {} });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeReservation, setActiveReservation] = useState(null);
  const [calOpen, setCalOpen] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const [lastUpdated, setLastUpdated] = useState(null);
  const [justUpdated, setJustUpdated] = useState(false);
  const prevRef = useRef({ date: null, byId: {} });
  const [search, setSearch] = useState("");
  const [recapOpen, setRecapOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [highlight, setHighlight] = useState(null);
  const [pendingMove, setPendingMove] = useState(null);
  const [pendingSwap, setPendingSwap] = useState(null);
  const [autoAssign, setAutoAssign] = useState(true);
  const [clearOpen, setClearOpen] = useState(false);
  const isMobile = useIsMobile();
  const [mobileTab, setMobileTab] = useState("board");
  const [assignFor, setAssignFor] = useState(null);
  const [editSpot, setEditSpot] = useState(null);
  const [orders, setOrders] = useState([]);
  const [guestDetail, setGuestDetail] = useState(null);
  const [cutoffTime, setCutoffTime] = useState(() => localStorage.getItem("noShowCutoff") || "13:30");

  const setCutoff = (v) => { if (v) { setCutoffTime(v); localStorage.setItem("noShowCutoff", v); } };
  const cutoffMin = useMemo(() => { const [h, m] = cutoffTime.split(":"); return (+h) * 60 + (+m); }, [cutoffTime]);

  const dateStr = format(date, "yyyy-MM-dd");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 6 } }),
  );

  const announceChanges = useCallback((changes) => {
    if (changes.length > 4) {
      toast.info(`${changes.length} reservations changed`, { description: "SimplyBook.me updates synced" });
      return;
    }
    changes.forEach((c) => {
      if (c.kind === "cancelled") toast.warning(`Cancelled: ${c.name}`, { description: "Removed in SimplyBook.me — spot freed" });
      else if (c.kind === "new") toast.info(`New booking: ${c.name}`);
      else toast.info(`Updated: ${c.name}`, { description: `Changed ${c.what}` });
    });
  }, []);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const res = await axios.get(`${API}/reservations`, { params: { date: dateStr } });
      const payload = res.data;
      const newById = {};
      (payload.reservations || []).forEach((r) => {
        newById[r.booking_id] = { name: r.guest_name, start: r.start_date, end: r.end_date, type: r.lounge_type, pax: r.party_size };
      });
      const prev = prevRef.current;
      if (silent && prev.date === dateStr && Object.keys(prev.byId).length) {
        const changes = [];
        Object.keys(prev.byId).forEach((id) => {
          if (!newById[id]) changes.push({ kind: "cancelled", name: prev.byId[id].name });
        });
        Object.keys(newById).forEach((id) => {
          const p = prev.byId[id], n = newById[id];
          if (!p) { changes.push({ kind: "new", name: n.name }); return; }
          const diffs = [];
          if (p.name !== n.name) diffs.push("name");
          if (p.start !== n.start || p.end !== n.end) diffs.push("time");
          if (p.type !== n.type) diffs.push("spot size");
          if (p.pax !== n.pax) diffs.push("guests");
          if (diffs.length) changes.push({ kind: "edited", name: n.name, what: diffs.join(", ") });
        });
        if (changes.length) announceChanges(changes);
      }
      prevRef.current = { date: dateStr, byId: newById };
      setData({ notes: {}, ...payload });
      if (typeof payload.auto_assign === "boolean") setAutoAssign(payload.auto_assign);
      if (payload.auto_assigned > 0)
        toast("Auto-assigned to matching spots", {
          description: `${payload.auto_assigned} ${payload.auto_assigned === 1 ? "booking" : "bookings"} placed — adjust any as needed`,
        });
      setLastUpdated(new Date());
      setNow(new Date());
      setJustUpdated(true);
      setTimeout(() => setJustUpdated(false), 1600);
    } catch (e) {
      toast.error("Could not reach SimplyBook", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); setRefreshing(false); }
  }, [dateStr, announceChanges]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => load(true), 30000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  const cutoffPassed = useMemo(() => {
    const today = format(now, "yyyy-MM-dd");
    if (dateStr < today) return true;
    if (dateStr > today) return false;
    return now.getHours() * 60 + now.getMinutes() >= cutoffMin;
  }, [now, dateStr, cutoffMin]);

  const noShowFor = useCallback(
    (occ) => cutoffPassed && occ && occ.status !== "arrived",
    [cutoffPassed],
  );

  const allocByLounge = useMemo(() => {
    const m = {}; data.allocations.forEach((a) => { m[a.lounge_id] = a; }); return m;
  }, [data.allocations]);

  const resByBooking = useMemo(() => {
    const m = {}; data.reservations.forEach((r) => { m[r.booking_id] = r; }); return m;
  }, [data.reservations]);

  const labelById = useMemo(() => {
    const m = {}; data.lounges.forEach((l) => { m[l.id] = l.label; }); return m;
  }, [data.lounges]);

  const unassigned = useMemo(() => {
    const rank = { large: 0, medium: 1, small: 2, umbrella: 3 };
    return data.reservations
      .filter((r) => !r.assigned_lounge_id && !r.gone)
      .sort((a, b) => {
        const ra = rank[a.lounge_type] ?? 9, rb = rank[b.lounge_type] ?? 9;
        if (ra !== rb) return ra - rb;
        return (a.start_date || "").localeCompare(b.start_date || "");
      });
  }, [data.reservations]);

  const goneList = useMemo(
    () => data.reservations.filter((r) => r.gone),
    [data.reservations],
  );

  const matchesSearch = useCallback((r) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const name = (r.guest_name || "").toLowerCase();
    const phone = (r.phone || "").replace(/\D/g, "");
    const qd = q.replace(/\D/g, "");
    return name.includes(q) || (qd.length >= 2 && phone.includes(qd));
  }, [search]);

  const filteredUnassigned = useMemo(
    () => unassigned.filter(matchesSearch),
    [unassigned, matchesSearch],
  );

  const assignedMatches = useMemo(() => {
    if (!search.trim()) return [];
    return data.reservations.filter((r) => r.assigned_lounge_id && matchesSearch(r));
  }, [search, data.reservations, matchesSearch]);

  const jumpTo = (loungeId) => {
    const el = document.querySelector(`[data-testid="lounge-tile-${loungeId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlight(loungeId);
      setTimeout(() => setHighlight(null), 2500);
    }
  };

  const loungesByType = useMemo(() => {
    const g = {}; data.lounges.forEach((l) => { (g[l.type] = g[l.type] || []).push(l); });
    Object.values(g).forEach((arr) => arr.sort((a, b) => a.num - b.num));
    return g;
  }, [data.lounges]);

  const summary = useMemo(() => {
    const s = {};
    TYPE_ORDER.forEach((t) => {
      const lounges = loungesByType[t] || [];
      const active = lounges.filter((l) => l.active !== false);
      const used = active.filter((l) => allocByLounge[l.id]).length;
      s[t] = { total: active.length, used, free: active.length - used };
    });
    s.arrived = data.allocations.filter((a) => a.status === "arrived").length;
    s.waiting = data.allocations.length - s.arrived;
    s.noShow = data.allocations.filter((a) => noShowFor(a)).length;
    s.gone = data.reservations.filter((r) => r.gone).length;
    return s;
  }, [loungesByType, allocByLounge, data.allocations, data.reservations, noShowFor]);

  const handleDragStart = (e) => setActiveReservation(e.active?.data?.current?.reservation || null);

  const commitAllocation = async (reservation, lounge) => {
    const crossType = reservation.lounge_type !== lounge.type;
    const status = reservation.status || "allocated";
    const optimistic = {
      id: `tmp-${reservation.booking_id}`, date: dateStr, lounge_id: lounge.id,
      booking_id: reservation.booking_id, guest_name: reservation.guest_name,
      service: reservation.service, lounge_type: reservation.lounge_type,
      start_date: reservation.start_date, end_date: reservation.end_date,
      phone: reservation.phone, email: reservation.email, status,
    };
    setData((prev) => ({
      ...prev,
      allocations: prev.allocations
        .filter((a) => a.booking_id !== reservation.booking_id && a.lounge_id !== lounge.id)
        .concat(optimistic),
      reservations: prev.reservations.map((r) =>
        r.booking_id === reservation.booking_id ? { ...r, assigned_lounge_id: lounge.id, gone: false } : r),
    }));
    try {
      await axios.post(`${API}/allocations`, {
        date: dateStr, lounge_id: lounge.id, booking_id: reservation.booking_id,
        guest_name: reservation.guest_name, service: reservation.service,
        lounge_type: reservation.lounge_type, start_date: reservation.start_date,
        end_date: reservation.end_date, phone: reservation.phone, email: reservation.email,
        status,
      });
      toast.success(`${reservation.guest_name} → ${lounge.label}`,
        crossType ? { description: `Different spot: booked ${reservation.lounge_type}, seated on ${lounge.type}` } : undefined);
      load(true);
    } catch (err) {
      toast.error("Assignment failed", { description: err?.response?.data?.detail });
      load(true);
    }
  };

  const commitSwap = async ({ aLounge, bLounge, aName, bName }) => {
    setData((prev) => ({
      ...prev,
      allocations: prev.allocations.map((al) =>
        al.lounge_id === aLounge ? { ...al, lounge_id: bLounge }
          : al.lounge_id === bLounge ? { ...al, lounge_id: aLounge } : al),
    }));
    try {
      await axios.post(`${API}/allocations/swap`, { date: dateStr, lounge_a: aLounge, lounge_b: bLounge });
      toast.success(`Swapped ${aName} ↔ ${bName}`);
      load(true);
    } catch { toast.error("Could not swap the guests"); load(true); }
  };

  const handleDragEnd = (e) => {
    setActiveReservation(null);
    const over = e.over;
    const reservation = e.active?.data?.current?.reservation;
    if (!over || !reservation) return;
    const lounge = over.data?.current?.lounge;
    if (!lounge) return;
    const fromLounge = e.active?.data?.current?.fromLounge;
    if (fromLounge && fromLounge === lounge.id) return; // dropped back on the same spot

    const targetOcc = allocByLounge[lounge.id];
    if (!targetOcc && lounge.active === false) {
      toast.error(`${lounge.label} is out of service`);
      return;
    }
    // Swap two seated guests when a seated guest is dropped onto an occupied spot
    if (fromLounge && targetOcc && targetOcc.booking_id !== reservation.booking_id) {
      const fromObj = data.lounges.find((l) => l.id === fromLounge);
      const mismatch = reservation.lounge_type !== lounge.type || targetOcc.lounge_type !== fromObj?.type;
      const payload = { aLounge: fromLounge, bLounge: lounge.id, aName: reservation.guest_name, bName: targetOcc.guest_name };
      if (mismatch) { setPendingSwap(payload); return; }
      commitSwap(payload);
      return;
    }

    if (reservation.lounge_type !== lounge.type) {
      setPendingMove({ reservation, lounge }); // ask for confirmation on a different-size spot
      return;
    }
    commitAllocation(reservation, lounge);
  };

  const handleToggleArrived = async (loungeId) => {
    const occ = allocByLounge[loungeId];
    if (!occ) return;
    const next = occ.status === "arrived" ? "allocated" : "arrived";
    setData((prev) => ({
      ...prev,
      allocations: prev.allocations.map((a) => (a.lounge_id === loungeId ? { ...a, status: next } : a)),
    }));
    try {
      await axios.patch(`${API}/allocations/status`, { date: dateStr, lounge_id: loungeId, status: next });
      if (next === "arrived") toast.success(`${occ.guest_name} arrived`);
      load(true);
    } catch { toast.error("Could not update status"); load(true); }
  };

  const handleGone = async (loungeId) => {
    const occ = allocByLounge[loungeId];
    if (!occ) return;
    setData((prev) => ({
      ...prev,
      allocations: prev.allocations.filter((a) => a.lounge_id !== loungeId),
      reservations: prev.reservations.map((r) =>
        r.booking_id === occ.booking_id ? { ...r, assigned_lounge_id: null, gone: true } : r),
    }));
    try {
      await axios.post(`${API}/allocations/gone`, { date: dateStr, booking_id: occ.booking_id });
      toast.success(`${occ.guest_name} marked gone — spot free`);
      load(true);
    } catch { toast.error("Could not mark gone"); load(true); }
  };

  const handleUndoGone = async (bookingId) => {
    setData((prev) => ({
      ...prev,
      reservations: prev.reservations.map((r) => (r.booking_id === bookingId ? { ...r, gone: false } : r)),
    }));
    try {
      await axios.delete(`${API}/allocations/gone`, { params: { date: dateStr, booking_id: bookingId } });
      load(true);
    } catch { load(true); }
  };

  const handleToggleAuto = async (v) => {
    setAutoAssign(v);
    try {
      await axios.put(`${API}/settings/auto-assign`, { enabled: v });
      toast.success(v ? "Auto-assign on" : "Auto-assign off",
        { description: v ? "New bookings will fill matching spots" : "New bookings stay in the list" });
      load(true);
    } catch { toast.error("Could not update auto-assign"); setAutoAssign(!v); }
  };

  const handleClearAll = async () => {
    setClearOpen(false);
    setData((prev) => ({
      ...prev,
      allocations: [],
      reservations: prev.reservations.map((r) => ({ ...r, assigned_lounge_id: null })),
    }));
    try {
      await axios.delete(`${API}/allocations/all`, { params: { date: dateStr } });
      if (autoAssign) { await axios.put(`${API}/settings/auto-assign`, { enabled: false }); setAutoAssign(false); }
      toast.success("All spots cleared", { description: "Auto-assign paused — place guests by hand" });
      load(true);
    } catch { toast.error("Could not clear the board"); load(true); }
  };

  const handleUnassign = async (loungeId) => {
    const occ = allocByLounge[loungeId];
    setData((prev) => ({
      ...prev,
      allocations: prev.allocations.filter((a) => a.lounge_id !== loungeId),
      reservations: prev.reservations.map((r) =>
        occ && r.booking_id === occ.booking_id ? { ...r, assigned_lounge_id: null } : r),
    }));
    try {
      await axios.delete(`${API}/allocations`, { params: { date: dateStr, lounge_id: loungeId } });
      toast.success(`${occ ? occ.guest_name : "Guest"} returned to the list`);
      load(true);
    } catch { toast.error("Could not return to list"); load(true); }
  };

  const handleSaveTags = async (cid, tags) => {
    setData((prev) => ({
      ...prev,
      reservations: prev.reservations.map((r) => (clientKey(r) === cid ? { ...r, tags } : r)),
    }));
    try { await axios.put(`${API}/tags`, { client_id: cid, tags }); toast.success("Tags updated"); load(true); }
    catch { load(true); }
  };

  const handleSaveNote = async (loungeId, note) => {
    setData((prev) => ({ ...prev, notes: { ...(prev.notes || {}), [loungeId]: note } }));
    try { await axios.put(`${API}/notes`, { date: dateStr, lounge_id: loungeId, note }); toast.success(note ? "Note saved" : "Note cleared"); load(true); }
    catch { load(true); }
  };

  const handleAddSpot = async (payload) => {
    try {
      const res = await axios.post(`${API}/lounges`, payload);
      toast.success(`${res.data.label} added`);
      load(true);
    } catch (e) {
      toast.error("Could not add spot", { description: e?.response?.data?.detail });
    }
  };

  const handleUpdateSpot = async (id, payload) => {
    try {
      const res = await axios.put(`${API}/lounges/${id}`, payload);
      toast.success(`${res.data.label} updated`);
      load(true);
    } catch (e) {
      toast.error("Could not update spot", { description: e?.response?.data?.detail });
    }
  };

  const handleDeleteSpot = async (id) => {
    try {
      await axios.delete(`${API}/lounges/${id}`);
      toast.success("Spot deleted");
      load(true);
    } catch (e) {
      toast.error("Could not delete spot", { description: e?.response?.data?.detail });
    }
  };

  const fetchOrders = useCallback(() => axios.get(`${API}/orders`, { params: { date: dateStr } })
    .then((r) => setOrders(r.data)).catch(() => {}), [dateStr]);
  useEffect(() => {
    fetchOrders();
    const t = setInterval(fetchOrders, 20000);
    return () => clearInterval(t);
  }, [fetchOrders]);

  const orderCountBySpot = useMemo(() => {
    const m = {};
    orders.filter((o) => o.status !== "served").forEach((o) => { m[o.spot_id] = (m[o.spot_id] || 0) + 1; });
    return m;
  }, [orders]);

  const villaLounges = useMemo(() => (data.lounges || []).filter((l) => l.type === "villa").sort((a, b) => (a.num || 0) - (b.num || 0)), [data.lounges]);

  const activeType = activeReservation?.lounge_type || null;
  const pull = usePullToRefresh(() => { if (!refreshing) load(true); }, isMobile);

  return (
    <>
      <DndContext sensors={sensors} collisionDetection={pointerWithin}
        onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="flex min-h-screen flex-col bg-sand-100 text-slate-900 md:h-screen print:hidden">
          {pull > 0 && (
            <div className="flex items-center justify-center overflow-hidden text-slate-400 md:hidden"
              style={{ height: pull }} data-testid="pull-indicator">
              <RefreshCw size={18} className={pull >= 70 ? "animate-spin text-brand-700" : ""}
                style={pull < 70 ? { transform: `rotate(${pull * 3}deg)` } : undefined} />
            </div>
          )}
          {/* Header */}
          <header className="flex flex-wrap items-center justify-between gap-3 border-b border-sand-300 bg-sand-50/80 px-5 py-3 backdrop-blur">
            <div className="flex items-center gap-3">
              <img src={LOGO} alt="Tribe Beach Lounge"
                className="h-11 w-11 rounded-full object-cover shadow-sm ring-2 ring-white" />
              <div>
                <h1 className="font-display text-2xl leading-none tracking-wide text-slate-900">
                  TRIBE <span className="text-brand-700">Beach Lounge</span>
                </h1>
                <p className="mt-1 text-[11px] font-medium uppercase tracking-[0.24em] text-slate-500">
                  Lounge Allocation · Tigaki, Kos
                </p>
                {lastUpdated && (
                  <p data-testid="last-updated"
                    className="mt-1 flex items-center gap-1.5 text-[11px] font-medium text-slate-400">
                    <span className={`inline-block h-1.5 w-1.5 rounded-full ${justUpdated ? "bg-emerald-500 animate-pulse" : "bg-emerald-500/50"}`} />
                    {justUpdated ? "Updated just now" : `Updated ${format(lastUpdated, "HH:mm")}`}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Popover open={calOpen} onOpenChange={setCalOpen}>
                <PopoverTrigger asChild>
                  <Button data-testid="date-picker-btn" variant="outline"
                    className="h-10 gap-2 rounded-full border-sand-300 bg-white font-medium">
                    <CalendarDays size={16} className="text-brand-700" />
                    {format(date, "EEE, dd MMM yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar mode="single" selected={date}
                    onSelect={(d) => { if (d) { setDate(d); setCalOpen(false); } }} initialFocus />
                </PopoverContent>
              </Popover>
              <Button data-testid="add-spot-btn" onClick={() => setAddOpen(true)} variant="outline"
                className="hidden h-10 gap-2 rounded-full border-sand-300 bg-white font-medium md:inline-flex">
                <Plus size={16} className="text-brand-700" />
                <span className="hidden lg:inline">Add spot</span>
              </Button>
              <Button data-testid="clear-all-btn" onClick={() => setClearOpen(true)} variant="outline"
                disabled={data.allocations.length === 0}
                className="hidden h-10 gap-2 rounded-full border-sand-300 bg-white font-medium md:inline-flex">
                <Eraser size={16} className="text-brand-700" />
                <span className="hidden lg:inline">Clear all</span>
              </Button>
              <Button data-testid="recap-btn" onClick={() => setRecapOpen(true)} variant="outline"
                className="hidden h-10 gap-2 rounded-full border-sand-300 bg-white font-medium md:inline-flex">
                <BarChart3 size={16} className="text-brand-700" />
                <span className="hidden lg:inline">Recap</span>
              </Button>
              <Button data-testid="print-btn" onClick={() => window.print()} variant="outline"
                className="hidden h-10 gap-2 rounded-full border-sand-300 bg-white font-medium md:inline-flex">
                <Printer size={16} className="text-brand-700" />
                <span className="hidden lg:inline">Print sheet</span>
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="more-menu-btn" variant="outline"
                    className="h-10 gap-1 rounded-full border-sand-300 bg-white font-medium">
                    <MoreHorizontal size={18} className="text-brand-700" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  <DropdownMenuItem data-testid="more-add-spot" onClick={() => setAddOpen(true)}>
                    <Plus size={14} className="mr-2" /> Add spot
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="more-clear-all" disabled={data.allocations.length === 0} onClick={() => setClearOpen(true)}>
                    <Eraser size={14} className="mr-2" /> Clear all
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="more-recap" onClick={() => setRecapOpen(true)}>
                    <BarChart3 size={14} className="mr-2" /> Recap
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="more-print" onClick={() => window.print()}>
                    <Printer size={14} className="mr-2" /> Print sheet
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button data-testid="refresh-btn" onClick={() => load(true)}
                className="h-10 gap-2 rounded-full bg-slate-900 font-medium text-white hover:bg-slate-800">
                <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
            </div>
          </header>

          {/* Summary / legend */}
          <div className="flex flex-nowrap items-center gap-2 overflow-x-auto border-b border-sand-300 bg-white/60 px-5 py-2.5 md:flex-wrap">
            {TYPE_ORDER.map((t) => (
              <SummaryPill key={t} label={TYPE_META[t].label} free={summary[t]?.free}
                total={summary[t]?.total} dot={TYPE_META[t].dot} testid={`summary-${t}`} />
            ))}
            <div className="mx-1 h-6 w-px bg-sand-300" />
            <div data-testid="summary-arrived"
              className="flex items-center gap-2 rounded-full bg-sea/10 px-3 py-1.5 ring-1 ring-sea/20">
              <CircleUser size={14} className="text-sea-600" />
              <span className="text-xs font-medium text-slate-500">Arrived</span>
              <span className="font-display text-sm tabular-nums text-sea-600">{summary.arrived ?? 0}</span>
            </div>
            <div data-testid="summary-waiting"
              className="flex items-center gap-2 rounded-full bg-brand/10 px-3 py-1.5 ring-1 ring-brand/20">
              <span className="text-xs font-medium text-slate-500">Wait</span>
              <span className="font-display text-sm tabular-nums text-brand-700">{summary.waiting ?? 0}</span>
            </div>
            {summary.gone > 0 && (
              <div data-testid="summary-gone"
                className="flex items-center gap-2 rounded-full bg-slate-500/15 px-3 py-1.5 ring-1 ring-slate-400/30">
                <span className="text-xs font-medium text-slate-500">Gone</span>
                <span className="font-display text-sm tabular-nums text-slate-600">{summary.gone}</span>
              </div>
            )}
            {summary.noShow > 0 && (
              <div data-testid="summary-noshow"
                className="flex items-center gap-2 rounded-full bg-destructive/10 px-3 py-1.5 ring-1 ring-destructive/30">
                <AlertTriangle size={14} className="text-destructive" />
                <span className="text-xs font-medium text-slate-500">No-show</span>
                <span className="font-display text-sm tabular-nums text-destructive">{summary.noShow}</span>
              </div>
            )}
            <div className="ml-auto flex items-center gap-3">
              <label className="flex cursor-pointer items-center gap-2" title="Auto-assign new bookings to matching spots">
                <Switch data-testid="auto-assign-toggle" checked={autoAssign} onCheckedChange={handleToggleAuto} />
                <span className="text-xs font-medium text-slate-500">Auto-assign</span>
              </label>
              <div className="h-6 w-px bg-sand-300" />
              <span className="hidden text-xs font-medium text-slate-500 sm:inline">No-show after</span>
              <input
                type="time"
                data-testid="cutoff-input"
                value={cutoffTime}
                onChange={(e) => setCutoff(e.target.value)}
                className="h-8 rounded-full border border-sand-300 bg-white px-2.5 text-xs font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand/40"
              />
            </div>
          </div>

          {/* Mobile tab switch */}
          <div className="flex gap-2 border-b border-sand-300 bg-white/60 px-4 py-2 md:hidden" data-testid="mobile-tabs">
            <button data-testid="tab-board" onClick={() => setMobileTab("board")}
              className={`flex-1 rounded-full py-2 text-sm font-semibold transition-colors ${mobileTab === "board" ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-sand-300"}`}>
              Beach map
            </button>
            <button data-testid="tab-list" onClick={() => setMobileTab("list")}
              className={`flex-1 rounded-full py-2 text-sm font-semibold transition-colors ${mobileTab === "list" ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-sand-300"}`}>
              Reservations ({unassigned.length})
            </button>
          </div>

          {/* Main */}
          <div className="grid grid-cols-1 gap-4 p-4 md:min-h-0 md:flex-1 md:grid-cols-12">
            {/* Sidebar */}
            <aside className={`col-span-1 ${mobileTab === "list" ? "flex" : "hidden"} min-h-0 flex-col overflow-hidden rounded-2xl border border-sand-300 bg-sand-50 md:col-span-4 md:flex lg:col-span-3`}>
              <div className="border-b border-sand-200 px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users size={16} className="text-brand-700" />
                    <h2 className="font-display text-lg tracking-wide">Reservations</h2>
                  </div>
                  <span data-testid="unassigned-count"
                    className="rounded-full bg-brand/15 px-2.5 py-0.5 text-xs font-bold text-brand-700">
                    {unassigned.length} to seat
                  </span>
                </div>
                <div className="relative mt-2.5">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    data-testid="guest-search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search guest by name or phone"
                    className="h-9 rounded-full border-sand-300 bg-white pl-8 text-sm"
                  />
                </div>
              </div>
              <div className="thin-scroll max-h-[60vh] space-y-2.5 overflow-y-auto p-3 md:max-h-none md:min-h-0 md:flex-1" data-testid="reservation-feed">
                {search.trim() && assignedMatches.length > 0 && (
                  <div className="rounded-xl border border-sand-200 bg-white p-2" data-testid="assigned-matches">
                    <p className="mb-1.5 px-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                      Already on the map
                    </p>
                    {assignedMatches.map((r) => (
                      <button
                        key={r.booking_id}
                        data-testid={`jump-${r.booking_id}`}
                        onClick={() => jumpTo(r.assigned_lounge_id)}
                        className="flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-sand-100"
                      >
                        <span className="truncate text-sm font-medium text-slate-700">{r.guest_name}</span>
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-brand/10 px-2 py-0.5 text-[11px] font-semibold text-brand-700">
                          <MapPin size={11} /> {labelById[r.assigned_lounge_id]}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                {loading ? (
                  <div className="flex h-40 items-center justify-center text-slate-400"><Loader2 className="animate-spin" /></div>
                ) : filteredUnassigned.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-2 py-16 text-center text-slate-400">
                    <CheckCircle2 size={28} className="text-sage" />
                    <p className="text-sm font-medium">
                      {search.trim()
                        ? "No guests match your search"
                        : data.reservations.length === 0 ? "No reservations this day" : "Everyone is seated"}
                    </p>
                  </div>
                ) : (
                  filteredUnassigned.map((r) => (
                    <div key={r.booking_id} className="animate-fade-up">
                      <GuestCard reservation={r} onSaveTags={handleSaveTags} tapToAssign={isMobile} onSelect={setAssignFor} />
                    </div>
                  ))
                )}
                {goneList.length > 0 && (
                  <div className="mt-3 border-t border-sand-200 pt-3" data-testid="gone-list">
                    <p className="mb-1.5 px-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                      Gone ({goneList.length})
                    </p>
                    {goneList.map((r) => (
                      <div key={r.booking_id} data-testid={`gone-${r.booking_id}`}
                        className="mb-1.5 flex items-center justify-between gap-2 rounded-lg bg-slate-300/60 px-3 py-2 text-slate-500">
                        <span className="truncate text-sm font-medium line-through">{r.guest_name}</span>
                        <button data-testid={`undo-gone-${r.booking_id}`} onClick={() => handleUndoGone(r.booking_id)}
                          className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold text-slate-600 ring-1 ring-slate-400 transition-colors hover:bg-white">
                          Undo
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </aside>

            {/* Beach map — 4 categories */}
            <main className={`thin-scroll relative col-span-1 ${mobileTab === "board" ? "block" : "hidden"} rounded-2xl border border-sand-300 bg-sand-50 p-3 sm:p-5 md:col-span-8 md:block md:min-h-0 md:overflow-y-auto lg:col-span-9`}>
              <div className="pointer-events-none absolute inset-0 rounded-2xl opacity-[0.05] mix-blend-multiply"
                style={{ backgroundImage: `url(${SAND_BG})`, backgroundSize: "cover" }} />
              <div className="relative space-y-6">
                <div className="flex items-center justify-center gap-2 rounded-full bg-sea/10 py-1.5 text-[11px] font-semibold uppercase tracking-[0.3em] text-sea-600">
                  <Waves size={13} /> Sea
                </div>

                {villaLounges.length > 0 && (
                  <section data-testid="villas-strip">
                    <div className="mb-2.5 flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full bg-brand" />
                      <h3 className="font-display text-base tracking-wide text-slate-700">Villas</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {villaLounges.map((v) => {
                        const c = orderCountBySpot[v.id] || 0;
                        return (
                          <button key={v.id} data-testid={`villa-chip-${v.id}`} onClick={() => setGuestDetail({ lounge: v })}
                            className={`relative flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold transition-colors ${c > 0 ? "border-brand bg-brand/5 text-brand-700" : "border-sand-300 bg-white text-slate-600 hover:border-brand"}`}>
                            {v.label}
                            {c > 0 && <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand px-1 text-[10px] font-bold text-white">{c}</span>}
                          </button>
                        );
                      })}
                    </div>
                  </section>
                )}

                {TYPE_ORDER.map((t) => {
                  const lounges = loungesByType[t] || [];
                  if (!lounges.length) return null;
                  return (
                    <section key={t} data-testid={`category-${t}`}>
                      <div className="mb-2.5 flex items-center gap-2">
                        <span className={`h-2.5 w-2.5 rounded-full ${TYPE_META[t].dot}`} />
                        <h3 className="font-display text-base tracking-wide text-slate-700">
                          {t === "umbrella" ? "Umbrellas" : `${TYPE_META[t].label} Gazebos`}
                        </h3>
                        <span className="text-xs text-slate-400">{summary[t]?.free}/{summary[t]?.total} free</span>
                      </div>
                      <div className={`grid ${MOBILE_COLS[t]} gap-2 md:flex md:flex-wrap md:gap-3`}>
                        {lounges.map((l) => {
                          const occ = allocByLounge[l.id];
                          return (
                            <div key={l.id} className={`w-full md:shrink-0 ${TILE_WIDTH[t]}`}>
                              <LoungeTile
                                lounge={l}
                                occupant={occ}
                                reservation={occ ? resByBooking[occ.booking_id] : null}
                                activeType={activeType}
                                noShow={noShowFor(occ)}
                                note={data.notes?.[l.id] || ""}
                                highlight={highlight === l.id}
                                onGone={handleGone}
                                onUnassign={handleUnassign}
                                onToggleArrived={handleToggleArrived}
                                onSaveTags={handleSaveTags}
                                onSaveNote={handleSaveNote}
                                onEditSpot={setEditSpot}
                                orderCount={orderCountBySpot[l.id] || 0}
                                onGuestClick={setGuestDetail}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </section>
                  );
                })}
              </div>
            </main>
          </div>
        </div>

        <DragOverlay dropAnimation={null}>
          {activeReservation ? (<div className="w-64"><GuestCard reservation={activeReservation} overlay /></div>) : null}
        </DragOverlay>
      </DndContext>

      <PrintSheet date={date} data={data} noShowFor={noShowFor} />
      <RecapDialog open={recapOpen} onOpenChange={setRecapOpen} data={data} noShowFor={noShowFor} date={date} />
      <AddSpotDialog open={addOpen} onOpenChange={setAddOpen} onAdd={handleAddSpot} />

      <EditSpotDialog spot={editSpot} onOpenChange={() => setEditSpot(null)}
        onSave={handleUpdateSpot} onDelete={handleDeleteSpot} />

      <GuestDetailDialog open={!!guestDetail} onOpenChange={() => setGuestDetail(null)}
        detail={guestDetail} onDone={fetchOrders}
        orders={guestDetail ? orders.filter((o) => o.spot_id === guestDetail.lounge.id) : []} />

      <AssignSheet
        reservation={assignFor}
        lounges={data.lounges}
        allocByLounge={allocByLounge}
        onOpenChange={() => setAssignFor(null)}
        onSeat={(res, lounge) => { setAssignFor(null); commitAllocation(res, lounge); }}
      />

      <Dialog open={!!pendingMove} onOpenChange={(o) => { if (!o) setPendingMove(null); }}>
        <DialogContent className="max-w-sm" data-testid="move-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-wide">Different spot type</DialogTitle>
            <DialogDescription className="sr-only">Confirm seating a guest on a different-size spot than they booked.</DialogDescription>
          </DialogHeader>
          {pendingMove && (
            <p className="text-sm leading-relaxed text-slate-600">
              <b className="text-slate-900">{pendingMove.reservation.guest_name}</b> booked a{" "}
              <b>{pendingMove.reservation.lounge_type}</b> spot, but{" "}
              <b>{pendingMove.lounge.label}</b> is a <b>{pendingMove.lounge.type}</b> spot. Seat them here anyway?
            </p>
          )}
          <div className="mt-4 flex gap-2">
            <button
              data-testid="move-cancel"
              onClick={() => setPendingMove(null)}
              className="h-10 flex-1 rounded-lg bg-white text-sm font-semibold text-slate-600 ring-1 ring-sand-300 transition-colors hover:bg-sand-100"
            >
              Cancel
            </button>
            <button
              data-testid="move-confirm"
              onClick={() => { const p = pendingMove; setPendingMove(null); if (p) commitAllocation(p.reservation, p.lounge); }}
              className="h-10 flex-1 rounded-lg bg-brand text-sm font-semibold text-white transition-colors hover:bg-brand-600"
            >
              Seat here
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={clearOpen} onOpenChange={setClearOpen}>
        <DialogContent className="max-w-sm" data-testid="clear-all-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-wide">Clear the whole board?</DialogTitle>
            <DialogDescription className="sr-only">Remove every allocation for this day and pause auto-assign.</DialogDescription>
          </DialogHeader>
          <p className="text-sm leading-relaxed text-slate-600">
            This frees <b className="text-slate-900">all {data.allocations.length} spots</b> for{" "}
            <b>{format(date, "EEE, dd MMM")}</b> and pauses Auto-assign so you can place guests by hand.
          </p>
          <div className="mt-4 flex gap-2">
            <button
              data-testid="clear-cancel"
              onClick={() => setClearOpen(false)}
              className="h-10 flex-1 rounded-lg bg-white text-sm font-semibold text-slate-600 ring-1 ring-sand-300 transition-colors hover:bg-sand-100"
            >
              Cancel
            </button>
            <button
              data-testid="clear-confirm"
              onClick={handleClearAll}
              className="h-10 flex-1 rounded-lg bg-destructive text-sm font-semibold text-white transition-colors hover:bg-destructive/90"
            >
              Clear all
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pendingSwap} onOpenChange={(o) => { if (!o) setPendingSwap(null); }}>
        <DialogContent className="max-w-sm" data-testid="swap-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-wide">Swap these guests?</DialogTitle>
            <DialogDescription className="sr-only">Confirm swapping two guests between spots of different types.</DialogDescription>
          </DialogHeader>
          {pendingSwap && (
            <p className="text-sm leading-relaxed text-slate-600">
              <b className="text-slate-900">{pendingSwap.aName}</b> and{" "}
              <b className="text-slate-900">{pendingSwap.bName}</b> will trade spots. This puts one or both on a different-size spot. Swap anyway?
            </p>
          )}
          <div className="mt-4 flex gap-2">
            <button
              data-testid="swap-cancel"
              onClick={() => setPendingSwap(null)}
              className="h-10 flex-1 rounded-lg bg-white text-sm font-semibold text-slate-600 ring-1 ring-sand-300 transition-colors hover:bg-sand-100"
            >
              Cancel
            </button>
            <button
              data-testid="swap-confirm"
              onClick={() => { const p = pendingSwap; setPendingSwap(null); if (p) commitSwap(p); }}
              className="h-10 flex-1 rounded-lg bg-brand text-sm font-semibold text-white transition-colors hover:bg-brand-600"
            >
              Swap
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

const SummaryPill = ({ label, free, total, dot, testid }) => (
  <div data-testid={testid} className="flex items-center gap-2 rounded-full border border-sand-300 bg-white px-3 py-1.5">
    <span className={`h-2 w-2 rounded-full ${dot}`} />
    <span className="text-xs font-medium text-slate-500">{label}</span>
    <span className="font-display text-sm tabular-nums text-slate-900">
      {free ?? "–"}<span className="text-slate-400">/{total ?? "–"}</span>
    </span>
  </div>
);
